/**
 * HTTP/SSE AI chat provider for molecule.dev.
 *
 * @example
 * ```typescript
 * // npm install @molecule/app-ai-chat-http --workspace=app
 * import { setProvider, requireProvider } from '@molecule/app-ai-chat'
 * import { createProvider } from '@molecule/app-ai-chat-http'
 *
 * setProvider(createProvider()) // same-origin base URL by default
 *
 * const chat = requireProvider()
 * const config = { endpoint: '/api/ai/chat' } // YOUR backend route (SSE) — the AI key lives there
 * await chat.sendMessage('Summarize my open orders', config, (event) => {
 *   if (event.type === 'text') appendTokens(event.content) // streamed chunks
 *   if (event.type === 'error') showError(event.message)
 * })
 * const history = await chat.loadHistory(config)
 * ```
 *
 * @remarks
 * POSTs each message to YOUR backend chat endpoint (`config.endpoint`, a RELATIVE path like
 * `/api/ai/chat` on the app's `baseUrl`) and reads the reply as an SSE stream — it does NOT talk
 * to an AI provider directly and holds NO AI key. Point `endpoint` at your own API, where the
 * provider key + `@molecule/api-ai` live; auth rides the session via the HTTP client
 * (cookie/bearer), so never attach a provider key or an absolute AI-provider URL here. See
 * `@molecule/app-ai-chat` for the safe-render rules.
 *
 *
 * Server contract (all on the ONE `config.endpoint` route): POST
 * `{ message, model?, attachments?, resume?, suppressUserMessage?,
 * automatic?, userInitiated? }` → SSE `data: <ChatStreamEvent JSON>` lines;
 * GET → `{ messages, streaming? }` plus any app-specific top-level fields (this
 * bond only reads `messages` + `streaming`; every other field rides through in
 * `provider.lastMeta` — e.g. an app that persists an agent `mode` reads it back as
 * `provider.lastMeta?.mode`); DELETE → clear history. Two conventions beyond
 * that route: a POST answered `409` means "conversation locked, still
 * streaming" — this bond retries automatically (up to 10 tries, 500 ms
 * doubling backoff) so return 409 rather than erroring; and Stop/unload
 * aborts POST to `<endpoint>-abort` (suffix on the pathname, query kept)
 * with `{ conversationId?, userInitiated? }` via sendBeacon.
 *
 * The 409 and the resume flag work together: a resume send (`resume: true`,
 * empty body) carries `Accept: text/event-stream`, so a server whose turn is
 * STILL RUNNING can attach the request to that live turn as a stream
 * subscriber (an `attached` frame, then the turn's remaining frames) instead
 * of 409ing. NEW sends deliberately do NOT send that Accept header — the
 * attach path never reads the request body, so a new message must keep the
 * 409-retry path to run after the current turn; serving it the attach
 * response would silently drop it.
 * `abortOnServer()`, `isServerStreaming`, and `lastMeta` are extensions on
 * `HttpChatProvider` beyond the core `ChatProvider` type. `loadHistory()`
 * returns `[]` on HTTP errors but REJECTS on network failure; wrap it.
 *
 * **A dropped stream is resumable, and a rolling server restart is survivable.**
 * The client remembers the newest SSE `id:` line it saw and sends it as
 * `Last-Event-ID` on the next resume POST; a server that supports resumption
 * continues from the event AFTER it instead of replaying the turn (one that
 * does not simply ignores the header — the app still converges from persisted
 * history). A resume POST answered "502/503/504" (an instance restarting during
 * a drain) is retried twice with jittered backoff, honoring `Retry-After`; a
 * FRESH send is never retried, because it may create a turn and there is no
 * idempotency key to collapse a duplicate. A transport drop mid-stream is
 * delivered as `{ type: 'error', transport: true }` so the caller resumes
 * rather than showing a terminal error.
 *
 * A non-ok response becomes an `error` event, and a JSON body's `error`,
 * `limitType`, `requiresSignup`, `billingAction` and `upgradeTier` are copied
 * onto it — so a limit refusal (402/429/403) reaches the UI with both the rule
 * that fired (`limitType`) and the remedy the backend resolved
 * (`billingAction`, e.g. `add_funds` vs `add_payment_method` vs `upgrade`,
 * with `upgradeTier: null` meaning there is no higher plan). Send those fields
 * from your API and the client's call-to-action is yours to decide; omit them
 * and the client can only guess.
 *
 * @module
 */
export * from './provider.js';
export * from './types.js';
import { createProvider } from './provider.js';
/** Pre-instantiated provider singleton. */
export const provider = createProvider();
//# sourceMappingURL=index.js.map